import { MapPin, Phone, Mail, Clock, Info } from 'lucide-react'
import ContactForm from '../components/ContactForm'
import SEO from '../components/SEO'

export default function ContactPage() {
  return (
    <div className="space-y-20 pb-16">
      <SEO
        title="Kontakt Rørbækklinikken | Gravene 28B, Viborg | Tlf 86 63 93 93"
        description="Kontakt Rørbækklinikken i Gravene 28B, 8800 Viborg. Ring på 86 63 93 93, send en besked eller find vej til klinikken. Vi glæder os til at byde dig velkommen."
      />
      
      {/* Page Header */}
      <section className="bg-[#F5F0E6]/40 py-16 border-b border-[#E6DFD3]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-4">
          <h1 className="font-serif text-4xl sm:text-5xl font-bold text-[#1E352F]">
            Kontakt klinikken
          </h1>
          <p className="text-base sm:text-lg text-[#1E352F]/80 leading-relaxed">
            Har du spørgsmål til vores behandlinger, eller ønsker du at booke en tid? Du er altid velkommen til at kontakte os på telefon, e-mail eller via formularen nedenfor.
          </p>
        </div>
      </section>

      {/* Contact Content Grid */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          
          {/* Left Column: Contact Info & Opening Hours */}
          <div className="lg:col-span-5 space-y-8">
            
            {/* Direct Contact Cards */}
            <div className="space-y-4">
              <h2 className="font-serif text-2xl font-bold text-[#1E352F]">
                Klinikoplysninger
              </h2>
              <p className="text-sm text-[#1E352F]/70 leading-relaxed">
                Rørbækklinikken I/S er beliggende i hyggelige, lyse lokaler i Viborg centrum, lige overfor de store parkeringsarealer ved Gravene.
              </p>
            </div>

            <div className="space-y-4">
              {/* Address */}
              <div className="flex gap-4 items-start bg-white p-5 rounded-xl border border-[#E6DFD3] shadow-sm">
                <div className="p-2.5 rounded-lg bg-[#F5F0E6] text-[#eebb53] shrink-0">
                  <MapPin className="w-5 h-5" />
                </div>
                <div className="space-y-1">
                  <p className="text-xs font-semibold text-[#1E352F]/50 uppercase tracking-wider">Adresse</p>
                  <p className="text-sm font-bold text-[#1E352F]">Gravene 28B, 8800 Viborg</p>
                  <a
                    href="https://maps.google.com/?q=Gravene+28A,+8800+Viborg"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block text-xs text-[#eebb53] hover:underline mt-1"
                  >
                    Vis på kort / rutevejledning
                  </a>
                </div>
              </div>

              {/* Phone */}
              <div className="flex gap-4 items-start bg-white p-5 rounded-xl border border-[#E6DFD3] shadow-sm">
                <div className="p-2.5 rounded-lg bg-[#F5F0E6] text-[#eebb53] shrink-0">
                  <Phone className="w-5 h-5" />
                </div>
                <div className="space-y-1">
                  <p className="text-xs font-semibold text-[#1E352F]/50 uppercase tracking-wider">Telefon</p>
                  <p className="text-sm font-bold text-[#1E352F]">
                    <a href="tel:86639393" className="hover:underline">86 63 93 93</a>
                  </p>
                  <p className="text-xs text-[#1E352F]/60">Vi svarer så vidt muligt, når vi ikke behandler.</p>
                </div>
              </div>

              {/* Email */}
              <div className="flex gap-4 items-start bg-white p-5 rounded-xl border border-[#E6DFD3] shadow-sm">
                <div className="p-2.5 rounded-lg bg-[#F5F0E6] text-[#eebb53] shrink-0">
                  <Mail className="w-5 h-5" />
                </div>
                <div className="space-y-1">
                  <p className="text-xs font-semibold text-[#1E352F]/50 uppercase tracking-wider">E-mail</p>
                  <p className="text-sm font-bold text-[#1E352F]">
                    <a href="mailto:im@muskelterapi.com" className="hover:underline">im@muskelterapi.com</a>
                  </p>
                  <p className="text-xs text-[#1E352F]/60">Vi besvarer e-mails inden for 24 timer på hverdage.</p>
                </div>
              </div>
            </div>

            {/* Opening Hours Card */}
            <div className="bg-[#F5F0E6] rounded-xl p-6 border border-[#E6DFD3] space-y-4">
              <h3 className="font-serif text-lg font-bold text-[#1E352F] flex items-center gap-2">
                <Clock className="w-5 h-5 text-[#eebb53]" />
                Telefon- & åbningstider
              </h3>
              <ul className="space-y-2 text-sm text-[#1E352F]/80">
                <li className="flex justify-between border-b border-[#E6DFD3] pb-1.5">
                  <span>Mandag - Torsdag</span>
                  <span className="font-bold">08:00 - 17:00</span>
                </li>
                <li className="flex justify-between border-b border-[#E6DFD3] pb-1.5">
                  <span>Fredag</span>
                  <span className="font-bold">08:00 - 15:00</span>
                </li>
                <li className="flex justify-between text-[#1E352F]/50">
                  <span>Lørdag - Søndag</span>
                  <span className="font-bold">Lukket</span>
                </li>
              </ul>
              <div className="pt-2 text-xs text-[#1E352F]/60 flex gap-2 items-start">
                <Info className="w-4 h-4 text-[#eebb53] shrink-0 mt-0.5" />
                <span>Hvis vi er optaget af behandling, kan du lægge en besked på telefonsvareren, så ringer vi tilbage hurtigst muligt.</span>
              </div>
            </div>

            {/* Company Details */}
            <div className="text-xs text-[#1E352F]/50 space-y-1">
              <p className="font-bold">Rørbækklinikken I/S</p>
              <p>CVR-nummer: 32389260</p>
              <p>Ejer: Inge-Merete Rørbæk</p>
            </div>

          </div>

          {/* Right Column: Contact Form */}
          <div className="lg:col-span-7">
            <ContactForm />
          </div>

        </div>
      </section>



    </div>
  )
}
